import React from 'react';
import { withRouter } from 'react-router-dom';
import moment from 'moment-timezone';
import { get, isEqual, isUndefined } from 'lodash';
import isEmpty from 'lodash/isEmpty';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputLabel,
  Tooltip,
  Typography,
  Link,
  Chip,
} from '@mui/material';

import VolumeUp from '@mui/icons-material/VolumeUp';
import VolumeOff from '@mui/icons-material/VolumeOff';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';

import {
  DEPLOYMENT_STATUSES, POSSESSION_STATUSES,
  getExtStatusReadable, getDeploymentStatusReadable,
  getPossessionStatusReadable, OpsBrandNames,
} from '../../lib/utils';
import MultiSelect from '../common/multi-select';
import BatteryLevel from '../common/battery-level';
import SmallMap from '../common/small-map';
import WarnBeforeDeleting from '../common/warn-before-deleting';
import SearchAttemptsList from '../common/search-attempts-list';
import VehicleHistory from './vehicle-history/vehicle-history';
import vehicleActions from '../../actions/vehicles';
import { EDIT, MARKET_VEHICLE_SUMMARY } from '../../access';
import { modalStyles } from '../../lib/styles';

const styles = {
  link: {
    textDecoration: 'none',
  },
};

const getLinkableFieldFromBrand = (brand) => {
  switch (brand) {
    case OpsBrandNames.LINK:
      return 'extId';
    default:
      return undefined;
  }
};

const getUrlForBrand = (brand, adminUrls) => {
  if (brand && adminUrls) {
    const lowerCaseBrand = brand.toLowerCase();
    if (adminUrls[lowerCaseBrand]) {
      return adminUrls[lowerCaseBrand];
    }
  }
};

const getUrl = (vehicle, market, adminUrls) => {
  if (!get(vehicle, 'brand') || !market || !adminUrls) return '';
  const { brand } = vehicle;
  const baseUrl = getUrlForBrand(brand, adminUrls);
  const identifier = get(vehicle, getLinkableFieldFromBrand(brand));
  if (isUndefined(baseUrl) || isUndefined(identifier)) return;

  let url = `${baseUrl}${identifier}`;
  if (brand === OpsBrandNames.LINK) {
    const fleetId = get(market, 'link.fleetIds[0]');
    if (!isUndefined(fleetId)) {
      url = `${baseUrl}${fleetId}/vehicles/${identifier}`;
    }
  }
  return url;
};

function VehicleEditToolbar({
  can, vehicle, handleStatusChange,
}) {
  return (
    <Grid container spacing={3}>
      <Grid item xs={6}>
        <InputLabel>Deployment</InputLabel>
        <MultiSelect
          isSingle
          value={[vehicle.extDeploymentStatus]}
          handleChange={(status) => {
            can(EDIT, MARKET_VEHICLE_SUMMARY) && handleStatusChange(status, 'extDeploymentStatus');
          }}
          options={DEPLOYMENT_STATUSES.map((status) => ({
            value: status,
            label: getDeploymentStatusReadable(status),
            isDisabled: false,
          }))}
        />
      </Grid>
      <Grid item xs={6}>
        <InputLabel>Possession</InputLabel>
        <MultiSelect
          isSingle
          value={[vehicle.extPossessionStatus]}
          handleChange={(status) => {
            can(EDIT, MARKET_VEHICLE_SUMMARY) && handleStatusChange(status, 'extPossessionStatus');
          }}
          options={POSSESSION_STATUSES.map((status) => ({
            value: status,
            label: getPossessionStatusReadable(status),
            isDisabled: false,
          }))}
        />
      </Grid>
    </Grid>
  );
}

function BasicVehicleMap({ vehicle, tz }) {
  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <SmallMap
          center={{
            lat: get(vehicle, 'geo.geoJson.coordinates[1]'),
            lng: get(vehicle, 'geo.geoJson.coordinates[0]'),
          }}
        />
        <InputLabel>
          {vehicle.geoUpdatedAt
            ? moment.tz(vehicle.geoUpdatedAt, tz).utc().format('MM/DD/YYYY hh:mm:ss a')
            : ''}
        </InputLabel>
      </Grid>
    </Grid>
  );
}

const RING_TIMEOUT = 60 * 1000;

class VehicleEditForm extends React.Component {
  constructor(props) {
    super(props);

    const { vehicle, showHistory } = props;

    this.state = {
      vehicle,
      unSavedChanges: false,
      displayEditForm: !showHistory, // if history isn't open, then the detail form inspect
      ringTimeout: 0,
    };

    this.handleSave = this.handleSave.bind(this);
    this.handleStatusChange = this.handleStatusChange.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.handleConfirmClose = this.handleConfirmClose.bind(this);
    this.handleRingVehicle = this.handleRingVehicle.bind(this);
  }

  closeModal() {
    const { vehicle: propsVehicle, onCancel } = this.props;
    const { vehicle: stateVehicle } = this.state;
    if (!isEqual(propsVehicle, stateVehicle)) {
      this.setState({ unSavedChanges: true });
    } else {
      onCancel();
    }
  }

  handleConfirmClose() {
    const { onCancel } = this.props;
    this.setState({
      unSavedChanges: false,
    });
    onCancel();
  }

  handleStatusChange(status, type) {
    const { vehicle } = this.state;
    const newVehicle = {
      ...vehicle,
      [type]: status,
    };
    this.setState({ vehicle: newVehicle });
  }

  handleSave() {
    const { onSave } = this.props;
    const { vehicle } = this.state;
    onSave(vehicle);
  }

  handleRingVehicle() {
    const { startRingingVehicle, stopRingingVehicle } = this.props;
    const { ringTimeout, vehicle } = this.state;

    if (ringTimeout) {
      stopRingingVehicle(vehicle._id);
      this.setState({ ringTimeout: 0 });
      clearTimeout(ringTimeout);
    } else {
      startRingingVehicle(vehicle._id);
      this.setState({
        ringTimeout: setTimeout(() => {
          this.setState({ ringTimeout: 0 });
        }, RING_TIMEOUT),
      });
    }
  }

  render() {
    const {
      vehicle, unSavedChanges, displayEditForm, ringTimeout,
    } = this.state;
    const {
      currentWrangler, isOpen, adminUrls, selectedMarket,
    } = this.props;
    const can = get(currentWrangler, 'access.can', () => true);
    let humanIdString = '';
    if (get(vehicle, 'humanId') && get(vehicle, 'type')) {
      humanIdString = `${vehicle.type} ${vehicle.humanId}`;
    }
    const closeButtonText = displayEditForm ? 'Cancel' : 'Close';
    const tz = get(selectedMarket, 'tz', 'America/New_York');
    return (
      <Dialog
        open={isOpen}
        fullWidth
        aria-labelledby="form-dialog-title"
        PaperProps={{ sx: modalStyles.dialog, style: { height: 'unset' } }}
        onClose={this.closeModal}
      >
        <DialogTitle id="form-dialog-title">
          <Grid spacing={2} container>
            <Grid item xs={12} sm={3}>
              <Grid container direction="column">
                <Grid item>
                  <Typography variant="h6">{humanIdString}</Typography>
                </Grid>
                {vehicle.needsMaintenance && (
                  <Grid item sx={{ my: 1 }}>
                    <Chip
                      color="secondary"
                      label="Needs maintenance"
                    />
                  </Grid>
                )}
                <Grid container direction="row" alignItems="center">
                  <Grid item>
                    <Typography variant="subtitle2">{getExtStatusReadable(vehicle.extStatus)}</Typography>
                  </Grid>
                  <Grid item>
                    <BatteryLevel batteryLevel={vehicle.batteryLevel} />
                  </Grid>
                </Grid>
              </Grid>
              <WarnBeforeDeleting
                isOpen={unSavedChanges}
                message="You have unsaved changes - are you sure you want to close?"
                onCancel={() => this.setState({ unSavedChanges: false })}
                onConfirm={this.handleConfirmClose}
              />
            </Grid>
            <Grid item xs={12} sm={9}>
              <Grid container>
                {displayEditForm && (
                  <VehicleEditToolbar
                    vehicle={vehicle}
                    can={can}
                    handleStatusChange={this.handleStatusChange}
                  />
                )}
              </Grid>
            </Grid>
            <Grid item xs={12}>
              {!isEmpty(vehicle.searchAttempts) && (
                <SearchAttemptsList searchAttempts={vehicle.searchAttempts} />
              )}
            </Grid>
          </Grid>
        </DialogTitle>
        <DialogContent>
          {displayEditForm && (
            <BasicVehicleMap vehicle={vehicle} tz={tz} />
          )}
          {!displayEditForm && (
            <VehicleHistory vehicle={vehicle} />
          )}
        </DialogContent>
        <DialogActions>
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid item>
              <Grid container alignItems="center" justifyContent="space-between">
                <Tooltip title={ringTimeout ? 'Stop Ringing Vehicle' : 'Start Ringing Vehicle'}>
                  <IconButton onClick={this.handleRingVehicle}>
                    {ringTimeout ? <VolumeOff style={{ color: 'red' }} /> : <VolumeUp />}
                  </IconButton>
                </Tooltip>
                {displayEditForm && (
                  <>
                    <Tooltip title="Show vehicle history">
                      <Link
                        component="button"
                        variant="body2"
                        onClick={() => {
                          this.setState(() => ({ displayEditForm: false }));
                        }}
                        style={styles.link}
                        sx={{ mr: 3 }}
                      >
                        <Typography variant="subtitle2">
                          History
                        </Typography>
                      </Link>
                    </Tooltip>
                    <Tooltip title="See vehicle on Link Admin" style={{ marginLeft: 25 }}>
                      <Link
                        target="_blank"
                        rel="noopener noreferrer"
                        href={getUrl(vehicle, selectedMarket, adminUrls)}
                        style={styles.link}
                      >
                        <Typography variant="subtitle2">
                          Link Admin
                        </Typography>
                      </Link>
                    </Tooltip>
                  </>
                )}
                {!displayEditForm && (
                  <Link
                    component="button"
                    variant="body2"
                    onClick={() => this.setState(() => ({ displayEditForm: true }))}
                    style={styles.link}
                  >
                    <Typography variant="subtitle2">
                      Details
                    </Typography>
                  </Link>
                )}
              </Grid>
            </Grid>
            <Grid item>
              <Button onClick={this.closeModal} color="secondary">
                {closeButtonText}
              </Button>
              {displayEditForm && (
                <Button onClick={this.handleSave} color="primary">
                  Save
                </Button>
              )}
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>
    );
  }
}

const mapDispatchToProps = (dispatch) => bindActionCreators({
  ...vehicleActions,
}, dispatch);

const mapStateToProps = ({ wranglers, markets }) => ({
  selectedMarket: markets.selectedMarket,
  currentWrangler: wranglers.currentWrangler,
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withRouter(VehicleEditForm));
